/**
 * Enqueues server-side canvas capture via POST .../capture-screenshot
 * (Temporal + headless Chromium). No client-side html-to-image capture.
 */

import { useCreateCanvasScreenshot } from "@/api/canvas";
import { useCanUndo } from "@liveblocks/react";
import { useCallback, useEffect, useRef } from "react";
import { useCanvasRoute } from "../context/CanvasRouteContext";
import { useCanvasPermissions } from "./useCanvasPermissions";

import createDebug from "debug";

const capturePreviewDebug = (scope: string) =>
  createDebug(`cohesive:capture-preview:${scope}`);

const logEnqueueServerHero = capturePreviewDebug("enqueue-server-hero");
const logLocalEditDetected = capturePreviewDebug("local-edit-detected");

/**
 * On the main canvas only, latches when the local user makes any storage
 * mutation and, on unmount, calls `POST .../capture-screenshot` so Neuraflow
 * screenshots `canvas-capture.html` and persists the thumbnail
 * (surfaced as the top-level `hero_image_url`). Skipped when the user
 * cannot edit or made
 * no local edits.
 *
 * Local-edit detection: Liveblocks' `useCanUndo()` only flips true when
 * THIS client has an undoable mutation queued — remote edits from other
 * clients never enter this client's undo stack. That maps cleanly to the
 * old Loro `subscribeLocalUpdates` semantics ("only fire on edits this
 * client originated"), which is what we want for thumbnail capture: we
 * shouldn't re-screenshot just because another collaborator typed in
 * a sticky note while this user passively viewed the canvas.
 *
 * The latch is one-way: once `canUndo` flips true we hold the bit even
 * if the user later undoes everything. That's intentional — even if the
 * net diff is zero, we want a fresh screenshot to correct any prior
 * render drift, and the cost is a single Temporal job per session.
 */
export function useCanvasPreview(): {
  enqueueServerHeroPreview: () => Promise<void>;
} {
  const { canEdit } = useCanvasPermissions();
  const { canvasId } = useCanvasRoute();
  const canUndo = useCanUndo();
  const hasLocalEditsRef = useRef(false);
  const createCanvasScreenshot = useCreateCanvasScreenshot();

  const enqueueServerHeroPreview = useCallback(async () => {
    if (!canvasId || !canEdit) return;
    logEnqueueServerHero("enqueueing server hero preview");
    await createCanvasScreenshot.mutateAsync(canvasId);
  }, [canvasId, canEdit, createCanvasScreenshot.mutateAsync]);

  const enqueueServerHeroPreviewRef = useRef(enqueueServerHeroPreview);
  enqueueServerHeroPreviewRef.current = enqueueServerHeroPreview;

  // Reset the latch when the user navigates between canvases (canvasId
  // changes) so a screenshot fires per-canvas, not per-session.
  useEffect(() => {
    hasLocalEditsRef.current = false;
  }, [canvasId]);

  // One-way latch: once canUndo flips true (first local mutation), we
  // remember it for the lifetime of this canvas mount.
  useEffect(() => {
    if (canUndo && !hasLocalEditsRef.current) {
      logLocalEditDetected("local edit detected");
      hasLocalEditsRef.current = true;
    }
  }, [canUndo]);

  useEffect(() => {
    return () => {
      if (!hasLocalEditsRef.current) return;
      void enqueueServerHeroPreviewRef.current();
    };
  }, []);

  return {
    enqueueServerHeroPreview,
  };
}
